/* Top-level dealer CRM shell with sidebar nav. Wraps Today / Pipeline /
   Conversations / Reports / Auto-Drip. Role-aware visibility. */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { isDealerUser } from "../lib/roles";
import QuickActionsModals, { QUICK_ACTION_KEYS } from "../components/QuickActionsModals";
import { VERSION } from "../version";

const NAV_GROUPS = [
  { group: "Pipeline", items: [
    { to: "/admin/today", label: "Dashboard", icon: "📊", roles: "all" },
    { to: "/admin/pipeline", label: "Pipeline", icon: "🚦", roles: "all" },
    { to: "/admin/conversations", label: "Messages", icon: "💬", roles: "all" },
  ]},
  { group: "Records", items: [
    { to: "/admin/crm", label: "Customers", icon: "👥", roles: "all" },
    { to: "/admin/inventory", label: "Vehicles", icon: "🚗", roles: "all" },
    { to: "/admin/inventory/vauto-feed", label: "vAuto Feed", icon: "🔌", roles: ["owner","admin","sales_manager","inventory"] },
    { to: "/admin/rebates", label: "Rebates / Offers", icon: "🏷️", roles: ["owner","admin"] },
    { to: "/admin/byers-pricing", label: "Byers MSRP Pricing", icon: "💵", roles: ["owner","admin"] },
    { to: "/admin/incentives", label: "Incentives & Rules", icon: "🎟️", roles: ["owner","admin"] },
    { to: "/admin/sms", label: "SMS Inbox-Plus", icon: "💬", roles: ["owner","admin"] },
    { to: "/admin/system/fees", label: "Dealer Fees", icon: "🧾", roles: ["owner","admin"] },
    { to: "/admin/crm?status=sold", label: "Deals", icon: "💰", roles: "all" },
    { to: "/admin/crm?tag=has_trade", label: "Trade Appraisals", icon: "📋", roles: "all" },
  ]},
  { group: "Paperwork", items: [
    { to: "/admin/documents/templates", label: "Documents & Forms", icon: "📄", roles: ["owner","admin","sales_manager"] },
    { to: "/admin/documents/quick-packs", label: "Quick Packs", icon: "📚", roles: ["owner","admin"] },
  ]},
  { group: "Growth", items: [
    { to: "/admin/reports", label: "Reports", icon: "📈", roles: ["owner","admin","general_manager","sales_manager"] },
    { to: "/admin/auto-drip", label: "AI Assistant", icon: "✨", roles: ["owner","admin","sales_manager","marketing"] },
    { to: "/admin/chatbot", label: "Chatbot Training", icon: "💬", roles: ["owner","admin","sales_manager","general_manager","super_admin","marketing"] },
  ]},
  { group: "Admin", items: [
    { to: "/admin/permissions", label: "Permissions", icon: "🔒", roles: ["owner","admin"] },
    { to: "/admin/security", label: "Security", icon: "🛡️", roles: ["owner","admin"] },
    { to: "/admin/system/backups", label: "Backup & Scanner", icon: "💾", roles: ["owner","admin","super_admin","general_manager"] },
    { to: "/dealer", label: "Dealer Portal (legacy)", icon: "🏢", roles: "all" },
  ]},
];

const QUICK_LINKS = [
  { to: "/admin/documents/templates", label: "+ Upload Template" },
  { to: "/admin/documents/quick-packs", label: "📚 Manage Quick Packs" },
  { to: "/admin/crm", label: "Open Customer Search" },
];

export default function DealerShell() {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickModal, setQuickModal] = useState(null);
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState("Today");

  useEffect(() => { if (ready && !isDealerUser(user)) navigate("/dealer-login"); }, [ready, user, navigate]);

  const canSee = (it) => it.roles === "all" || (Array.isArray(it.roles) && it.roles.includes(user?.role));

  const onSearch = (e) => {
    e.preventDefault();
    const q = search.trim();
    if (!q) return;
    navigate(`/admin/crm?q=${encodeURIComponent(q)}`);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="dealer-shell">
      {/* Sidebar */}
      <aside className={`${collapsed ? "w-16" : "w-56"} ${mobileOpen ? "fixed inset-y-0 left-0 z-40" : "hidden md:flex md:flex-col"} bg-[#061426] text-white shrink-0 sticky top-0 self-start h-screen overflow-y-auto transition-all`}>
        <div className="p-4 border-b border-white/10">
          {!collapsed && <>
            <div className="text-[10px] uppercase tracking-[0.2em] text-blue-300 font-bold">Dealer Command</div>
            <div className="font-extrabold text-lg mt-0.5">Scott Sells Chevys</div>
            <div className="text-[10px] text-white/60 mt-1 truncate">{user?.name || user?.email} · <span className="text-blue-300">{user?.role}</span></div>
            <div className="text-[10px] text-white/40 mt-0.5" data-testid="admin-version">Version {VERSION}</div>
          </>}
          {collapsed && <div className="text-blue-300 font-extrabold text-center">SSC</div>}
        </div>
        <nav className="py-2 flex-1">
          {NAV_GROUPS.map(g => {
            const items = g.items.filter(canSee);
            if (!items.length) return null;
            return (
              <div key={g.group} className="mb-2">
                {!collapsed && <div className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-wider text-white/40 font-bold">{g.group}</div>}
                {items.map(n => (
                  <NavLink key={n.to + n.label} to={n.to}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-2 text-sm hover:bg-white/10 ${isActive ? "bg-blue-600/30 border-l-4 border-blue-400 font-semibold" : "border-l-4 border-transparent"}`}
                    data-testid={`shell-nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                    <span className="w-5 text-center">{n.icon}</span>
                    {!collapsed && <span>{n.label}</span>}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>
        <button onClick={() => setCollapsed(v => !v)} className="hidden md:block text-[10px] text-white/40 hover:text-white px-4 py-2 border-t border-white/10" data-testid="shell-collapse">
          {collapsed ? "▶" : "◀ Collapse"}
        </button>
      </aside>

      {mobileOpen && <div className="fixed inset-0 bg-black/50 z-30 md:hidden" onClick={() => setMobileOpen(false)} />}

      <main className="flex-1 min-w-0 overflow-x-hidden flex flex-col">
        {/* Top utility bar */}
        <div className="bg-[#061426] text-white border-b border-white/10 sticky top-0 z-20" data-testid="shell-topbar">
          <div className="flex items-center gap-2 px-3 py-2">
            <button onClick={() => setMobileOpen(true)} className="md:hidden text-white/70 hover:text-white px-2" data-testid="shell-mobile-open">☰</button>
            <form onSubmit={onSearch} className="flex-1 max-w-2xl relative">
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="🔍 Search by name, phone, VIN, stock #, deal code…"
                className="w-full bg-white/5 border border-white/10 rounded px-3 py-1.5 text-sm placeholder-white/50 focus:outline-none focus:border-blue-400"
                data-testid="shell-global-search" />
            </form>
            <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}
              className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs"
              data-testid="shell-date-range">
              <option>Today</option><option>Week</option><option>Month</option><option>Year</option><option>Custom</option>
            </select>
            <div className="relative">
              <button onClick={() => setQuickOpen(v => !v)}
                className="bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold px-3 py-1.5 rounded"
                data-testid="shell-quick-actions">+ New ▾</button>
              {quickOpen && (
                <div className="absolute right-0 mt-1 bg-white text-[#061426] rounded shadow-lg border min-w-[220px] z-30" data-testid="shell-quick-menu">
                  {QUICK_ACTION_KEYS.map(qa => (
                    <button key={qa.key} type="button"
                      onClick={() => { setQuickModal(qa.key); setQuickOpen(false); }}
                      className="block w-full text-left px-3 py-2 text-sm hover:bg-blue-50"
                      data-testid={qa.testid}>{qa.label}</button>
                  ))}
                  <div className="border-t my-1"></div>
                  {QUICK_LINKS.map(qa => (
                    <Link key={qa.to + qa.label} to={qa.to} onClick={() => setQuickOpen(false)}
                      className="block px-3 py-2 text-sm hover:bg-blue-50 text-[#061426]/80"
                      data-testid={`shell-qa-${qa.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>{qa.label}</Link>
                  ))}
                </div>
              )}
            </div>
            <Link to="/admin/security" className="text-white/70 hover:text-white text-sm px-1" title="Notifications" data-testid="shell-notifications">🔔</Link>
            <a href="/" target="_blank" rel="noreferrer" className="text-white/70 hover:text-white text-xs flex items-center gap-1" data-testid="shell-view-site">↗ View site</a>
            <button onClick={async () => { try { await logout?.(); } catch (err) { console.warn("logout failed", err); } navigate("/dealer-login"); }}
              className="text-white/70 hover:text-white text-sm px-1" title="Sign out" data-testid="shell-signout">⎋</button>
          </div>
        </div>
        <Outlet />
        <QuickActionsModals openKey={quickModal} onClose={() => setQuickModal(null)} />
      </main>
    </div>
  );
}

// =============================================================================
// TODAY — role-aware dashboard
// =============================================================================
export function TodayPage() {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { const r = await api.get("/admin/today"); setData(r.data); } catch (e) { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading>Loading Today…</Loading>;
  const k = data.kpis || {};
  return (
    <Page title={`Today · ${data.user?.name || ""}`} subtitle={`Role: ${data.user?.role} · ${new Date(data.now).toLocaleString()}`} testid="page-today">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="today-kpis">
        <Kpi label="Overdue Tasks" value={k.tasks_overdue} accent={k.tasks_overdue ? "red" : null} />
        <Kpi label="Tasks Today"   value={k.tasks_today} />
        <Kpi label="Appts Today"   value={k.appts_today} />
        <Kpi label="Hot Leads"     value={k.hot_leads}  accent="amber" />
        <Kpi label="New Today"     value={k.new_today} />
        <Kpi label="Sold Today"    value={k.sold_today} accent="emerald" />
        <Kpi label="Gross Today"   value={`$${(k.gross_today || 0).toLocaleString()}`} accent="emerald" />
        <Kpi label="Cold Leads"    value={k.cold_leads} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Box title={`Tasks (overdue + today)`}>
          {(data.top_tasks || []).length === 0 && <Empty>No tasks queued. Nice work.</Empty>}
          <ul className="divide-y">
            {(data.top_tasks || []).map(t => (
              <li key={t.id} className="py-2 text-sm">
                <Link to={`/admin/crm/customer/${t.customer_id}`} className="font-semibold hover:underline">{t.title}</Link>
                <div className="text-xs text-gray-500">{t.task_type} · {t.due_at ? new Date(t.due_at).toLocaleString() : "No due"} · {t.priority}</div>
              </li>
            ))}
          </ul>
        </Box>
        <Box title="Next 5 Appointments">
          {(data.upcoming_appts || []).length === 0 && <Empty>Nothing scheduled.</Empty>}
          <ul className="divide-y">
            {(data.upcoming_appts || []).map(a => (
              <li key={a.id} className="py-2 text-sm">
                <Link to={`/admin/crm/customer/${a.customer_id}`} className="font-semibold hover:underline">{a.appt_type}</Link>
                <div className="text-xs text-gray-500">{a.starts_at ? new Date(a.starts_at).toLocaleString() : ""} · {a.location || ""}</div>
              </li>
            ))}
          </ul>
        </Box>
        <Box title="Recent Hot Leads">
          {(data.recent_hot || []).length === 0 && <Empty>No hot leads right now.</Empty>}
          <ul className="divide-y">
            {(data.recent_hot || []).map(l => (
              <li key={l.id} className="py-2 text-sm">
                <Link to={`/admin/crm/customer/${l.id}`} className="font-semibold hover:underline">{l.name}</Link>
                <div className="text-xs text-gray-500">{l.vehicle_interest || l.vehicle_title || "—"} · {l.lead_source || ""}</div>
              </li>
            ))}
          </ul>
        </Box>
      </div>
    </Page>
  );
}

// =============================================================================
// PIPELINE — kanban-ish board grouped by status
// =============================================================================
const STAGE_COLORS = {
  new: "bg-blue-100 text-blue-700",
  working: "bg-amber-100 text-amber-700",
  appointment: "bg-purple-100 text-purple-700",
  sold: "bg-emerald-100 text-emerald-700",
  lost: "bg-red-100 text-red-700",
  bad: "bg-gray-200 text-gray-600",
};

export function PipelinePage() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/pipeline").then(r => setData(r.data)); }, []);
  if (!data) return <Loading>Loading pipeline…</Loading>;
  return (
    <Page title="Pipeline" subtitle="Drag-and-drop coming soon · click a card to open" testid="page-pipeline">
      <div className="flex gap-3 overflow-x-auto pb-4" data-testid="pipeline-board">
        {data.stages.map(stage => (
          <div key={stage} className="bg-white border border-gray-200 rounded-lg w-72 shrink-0 flex flex-col">
            <div className={`px-3 py-2 border-b border-gray-200 flex items-center justify-between ${STAGE_COLORS[stage] || "bg-gray-100"}`}>
              <span className="font-bold uppercase text-xs">{stage}</span>
              <span className="text-xs font-mono">{data.counts[stage] || 0}</span>
            </div>
            <div className="p-2 space-y-2 min-h-[200px] max-h-[70vh] overflow-y-auto" data-testid={`pipeline-col-${stage}`}>
              {(data.leads[stage] || []).map(l => (
                <Link key={l.id} to={`/admin/crm/customer/${l.id}`}
                  className="block p-2 rounded border border-gray-200 hover:border-blue-400 hover:bg-blue-50/30 text-sm"
                  data-testid={`pipeline-card-${l.id}`}>
                  <div className="font-semibold text-[#061426] truncate">{l.name || "—"}</div>
                  <div className="text-xs text-gray-500 truncate">{l.vehicle_interest || l.vehicle_title || ""}</div>
                  <div className="flex items-center gap-1 mt-1 text-[10px]">
                    {l.temperature && <span className="px-1.5 py-0.5 rounded bg-gray-100">{l.temperature}</span>}
                    <span className="text-gray-400 ml-auto">{(l.last_touch_at || l.created_at || "").slice(0, 10)}</span>
                  </div>
                </Link>
              ))}
              {(data.leads[stage] || []).length === 0 && <div className="text-center text-xs text-gray-400 py-6">Empty</div>}
            </div>
          </div>
        ))}
      </div>
    </Page>
  );
}

// =============================================================================
// CONVERSATIONS — unified log
// =============================================================================
const CHANNEL_ICON = { text: "💬", call: "📞", email: "✉️", chat: "🌐", ai: "🤖", note: "📝" };

export function ConversationsPage() {
  const [data, setData] = useState(null);
  const [channel, setChannel] = useState("");
  const [direction, setDirection] = useState("");
  const [selected, setSelected] = useState(() => new Set()); // chat_session ids
  const [confirm, setConfirm] = useState(null); // {type:"single"|"bulk", ids:[]}
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    const params = {};
    if (channel) params.channel = channel;
    if (direction) params.direction = direction;
    const r = await api.get("/admin/conversations", { params });
    setData(r.data);
  }, [channel, direction]);
  useEffect(() => { load(); }, [load]);

  const isChat = (it) => it.source_kind === "chat_session";
  const chatItems = (data?.items || []).filter(isChat);
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const allChatSelected = chatItems.length > 0 && chatItems.every((it) => selected.has(it.id));
  const toggleAll = () => {
    if (allChatSelected) setSelected(new Set());
    else setSelected(new Set(chatItems.map((it) => it.id)));
  };

  const doDelete = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      if (confirm.type === "single") {
        await api.delete(`/admin/conversations/chat/${confirm.ids[0]}`);
      } else {
        await api.post("/admin/conversations/chat/bulk_delete", { ids: confirm.ids });
      }
      setSelected(new Set());
      setConfirm(null);
      await load();
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <Loading>Loading conversations…</Loading>;

  return (
    <Page title="Conversations" subtitle="Unified log · calls, texts, emails, website chat, and AI" testid="page-conversations">
      <div className="flex flex-wrap gap-2 mb-3">
        <FilterChip active={!channel} onClick={() => setChannel("")}>All ({data.count})</FilterChip>
        {Object.entries(CHANNEL_ICON).map(([k, ic]) => (
          <FilterChip key={k} active={channel === k} onClick={() => setChannel(k)} testid={`conv-filter-${k}`}>
            {ic} {k} {data.counts && data.counts[k] ? `(${data.counts[k]})` : ""}
          </FilterChip>
        ))}
        <div className="flex-1" />
        <select value={direction} onChange={e => setDirection(e.target.value)} className="border border-gray-300 rounded p-2 text-sm" data-testid="conv-direction">
          <option value="">Any direction</option>
          <option value="inbound">Inbound</option>
          <option value="outbound">Outbound</option>
        </select>
      </div>

      {/* Bulk action bar — only renders when at least one chat is selected. */}
      {selected.size > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 mb-3 flex items-center gap-2 flex-wrap" data-testid="conv-bulk-bar">
          <span className="text-sm font-bold text-amber-900">{selected.size} chat selected</span>
          <button onClick={() => setSelected(new Set())} className="text-xs text-amber-800 hover:underline" data-testid="conv-bulk-clear">Clear</button>
          <div className="flex-1" />
          <button
            onClick={() => setConfirm({ type: "bulk", ids: Array.from(selected) })}
            className="ssc-btn ssc-btn-outline !py-1.5 !text-xs !text-red-600 !border-red-300 hover:!bg-red-50"
            data-testid="conv-bulk-delete"
          >
            Delete {selected.size} conversation{selected.size === 1 ? "" : "s"}
          </button>
        </div>
      )}

      {/* "Select all chats" affordance — only meaningful when chats are visible. */}
      {chatItems.length > 0 && (
        <label className="flex items-center gap-2 text-xs text-gray-600 mb-2 cursor-pointer" data-testid="conv-select-all-wrap">
          <input type="checkbox" checked={allChatSelected} onChange={toggleAll} data-testid="conv-select-all" />
          Select all website chats on this page
        </label>
      )}

      <div className="bg-white rounded-lg border border-gray-200 divide-y" data-testid="conv-list">
        {(data.items || []).map(it => {
          const chat = isChat(it);
          const checked = chat && selected.has(it.id);
          return (
            <div key={`${it.source_kind}-${it.id}`} className={`flex items-start gap-2 p-3 hover:bg-blue-50/30 ${checked ? "bg-blue-50/60" : ""}`} data-testid={`conv-item-${it.id}`}>
              {chat ? (
                <input
                  type="checkbox"
                  className="mt-1 shrink-0"
                  checked={checked}
                  onChange={() => toggleOne(it.id)}
                  data-testid={`conv-select-${it.id}`}
                  aria-label="Select chat conversation"
                />
              ) : (
                <span className="w-4 h-4 mt-1 shrink-0" aria-hidden />
              )}
              <Link to={`/admin/crm/customer/${it.customer_id}`} className="flex-1 min-w-0 block">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-lg">{CHANNEL_ICON[it.channel] || "💬"}</span>
                  <span className="font-bold uppercase text-xs text-gray-500">{it.channel}</span>
                  <span className="text-xs text-gray-400">· {it.direction}</span>
                  <span className="text-xs text-gray-400 ml-auto">{it.at ? new Date(it.at).toLocaleString() : ""}</span>
                </div>
                <div className="text-sm text-[#061426] mt-1 truncate">{it.preview || "(no preview)"}</div>
                <div className="text-xs text-gray-500">{it.user_name}</div>
              </Link>
              {chat && (
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); setConfirm({ type: "single", ids: [it.id], preview: it.preview }); }}
                  className="shrink-0 p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
                  title="Delete this chat conversation"
                  data-testid={`conv-delete-${it.id}`}
                  aria-label="Delete chat conversation"
                >
                  <Trash2 size={15} />
                </button>
              )}
            </div>
          );
        })}
        {(data.items || []).length === 0 && <div className="p-6 text-center text-gray-400">No conversations yet for this filter.</div>}
      </div>

      {/* Confirmation modal */}
      {confirm && (
        <div className="fixed inset-0 z-[60] bg-black/55 grid place-items-center p-3" data-testid="conv-delete-modal">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5 sm:p-6">
            <h3 className="font-extrabold text-[var(--ssc-navy)]">
              {confirm.type === "single"
                ? "Are you sure you want to delete this chat conversation? This cannot be undone."
                : "Are you sure you want to delete these chat conversations? This cannot be undone."}
            </h3>
            <p className="text-xs text-gray-500 mt-2">
              The chat will be hidden from your inbox. Customer, lead, vehicle, and deal records stay intact.
            </p>
            <div className="mt-5 flex flex-wrap gap-2 justify-end">
              <button type="button" onClick={() => setConfirm(null)} className="ssc-btn ssc-btn-outline" data-testid="conv-delete-cancel">Cancel</button>
              <button
                type="button"
                onClick={doDelete}
                disabled={busy}
                className="ssc-btn ssc-btn-primary !bg-red-600 hover:!bg-red-700"
                data-testid="conv-delete-confirm"
              >
                {busy ? "Deleting…" : confirm.type === "single" ? "Delete Conversation" : `Delete ${confirm.ids.length} Conversations`}
              </button>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}

// =============================================================================
// REPORTS — store performance summary
// =============================================================================
export function ReportsPage() {
  const [data, setData] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [days, setDays] = useState(30);
  useEffect(() => {
    api.get("/admin/reports", { params: { days } }).then(r => setData(r.data));
    api.get("/byod/funnel", { params: { days } }).then(r => setFunnel(r.data)).catch(() => setFunnel(null));
  }, [days]);
  if (!data) return <Loading>Loading reports…</Loading>;
  const t = data.totals || {};
  const maxDaily = Math.max(1, ...(data.daily || []).map(d => Math.max(d.new, d.sold)));
  return (
    <Page title="Reports" subtitle={`Window: last ${days} days`} testid="page-reports">
      <div className="flex gap-2 mb-3">
        {[7, 14, 30, 90].map(n => (
          <button key={n} onClick={() => setDays(n)}
            className={`text-xs px-3 py-1 rounded-full border ${days === n ? "bg-[#006DFF] text-white border-[#006DFF]" : "bg-white border-gray-300"}`}
            data-testid={`reports-days-${n}`}>{n} days</button>
        ))}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Kpi label="Total Leads" value={t.total_leads || 0} />
        <Kpi label="Sold" value={t.sold || 0} accent="emerald" />
        <Kpi label="Lost" value={t.lost || 0} accent="red" />
        <Kpi label="Win Rate" value={`${t.win_rate || 0}%`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Box title={`Daily New vs Sold (last ${(data.daily || []).length} days)`}>
          <div className="flex items-end gap-1 h-40 mt-2" data-testid="reports-daily">
            {(data.daily || []).map(d => (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${d.new} new · ${d.sold} sold`}>
                <div className="w-full bg-blue-300 rounded-t" style={{ height: `${(d.new / maxDaily) * 80}%` }}></div>
                <div className="w-full bg-emerald-500 rounded-t" style={{ height: `${(d.sold / maxDaily) * 80}%` }}></div>
              </div>
            ))}
          </div>
          <div className="flex gap-3 text-xs mt-2"><span><span className="inline-block w-3 h-3 bg-blue-300 mr-1"></span>New</span><span><span className="inline-block w-3 h-3 bg-emerald-500 mr-1"></span>Sold</span></div>
        </Box>

        <Box title="Lead Sources">
          <table className="w-full text-sm" data-testid="reports-sources">
            <thead className="text-gray-500"><tr><th className="text-left">Source</th><th className="text-right">Leads</th><th className="text-right">Sold</th></tr></thead>
            <tbody>
              {(data.by_source || []).map(s => (
                <tr key={s.source} className="border-t"><td className="py-1">{s.source}</td><td className="text-right font-mono">{s.leads}</td><td className="text-right font-mono text-emerald-700">{s.sold}</td></tr>
              ))}
            </tbody>
          </table>
        </Box>

        {data.salesperson_leaderboard?.length > 0 && (
          <Box title="Salesperson Leaderboard">
            <table className="w-full text-sm" data-testid="reports-leaderboard">
              <thead className="text-gray-500"><tr><th className="text-left">Salesperson</th><th className="text-right">New</th><th className="text-right">Sold</th></tr></thead>
              <tbody>
                {data.salesperson_leaderboard.map((s, i) => (
                  <tr key={i} className="border-t"><td className="py-1">{s.name}</td><td className="text-right font-mono">{s.new}</td><td className="text-right font-mono text-emerald-700">{s.sold}</td></tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}

        {funnel?.ok && (
          <Box title={`Build My Own Deal · funnel (last ${funnel.days} days)`}>
            <div className="grid grid-cols-3 gap-3 mb-3" data-testid="byod-funnel-summary">
              <Kpi label="Started" value={funnel.totals.started} />
              <Kpi label="Submitted" value={funnel.totals.completed} accent="emerald" />
              <Kpi label="Conversion" value={`${funnel.totals.overall_conversion_pct}%`} />
            </div>
            <div data-testid="byod-funnel-bars">
              {(() => {
                const max = Math.max(1, ...funnel.steps.map((s) => s.reached));
                return funnel.steps.map((s, i) => {
                  const widthPct = Math.max(2, (s.reached / max) * 100);
                  const isFinal = s.key === "submit";
                  return (
                    <div key={s.key} className="flex items-center gap-2 mb-1.5" data-testid={`byod-funnel-step-${s.key}`}>
                      <div className="w-24 text-xs text-gray-600 font-medium truncate">{i + 1}. {s.label}</div>
                      <div className="flex-1 relative h-6 bg-gray-100 rounded">
                        <div
                          className={`h-full rounded ${isFinal ? "bg-emerald-500" : "bg-[#006DFF]"} transition-all`}
                          style={{ width: `${widthPct}%` }}
                        />
                        <div className="absolute inset-0 flex items-center justify-end pr-2 text-[11px] font-mono font-bold text-gray-800">
                          {s.reached}
                        </div>
                      </div>
                      <div className={`w-14 text-right text-[10px] font-mono ${s.drop_pct > 0 ? "text-red-600" : "text-gray-400"}`} title="Drop-off vs previous step">
                        {s.drop_pct > 0 ? `−${s.drop_pct}%` : "—"}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
            <div className="text-[11px] text-gray-500 mt-2 leading-snug">
              Bars show <strong>cumulative reach</strong> per step (anyone who reached step N OR later).
              The red number is the drop-off vs the previous step. Submit count = completed lead submissions.
            </div>
          </Box>
        )}
      </div>
    </Page>
  );
}

// =============================================================================
// AUTO-DRIP — campaigns + recent sends
// =============================================================================
export function AutoDripPage() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/auto-drip").then(r => setData(r.data)); }, []);
  if (!data) return <Loading>Loading auto-drip…</Loading>;
  return (
    <Page title="Auto-Drip" subtitle="Campaigns + send activity" testid="page-auto-drip">
      <Box title={`Campaigns (${data.campaigns?.length || 0})`}>
        {(!data.campaigns || data.campaigns.length === 0) && <Empty>No drip campaigns configured yet. (Connect via Settings → Automation.)</Empty>}
        <ul className="divide-y" data-testid="drip-campaigns">
          {(data.campaigns || []).map((c, i) => (
            <li key={i} className="py-2 text-sm">
              <div className="font-semibold">{c.name || c.title || "(unnamed)"}</div>
              <div className="text-xs text-gray-500">{c.trigger || ""} · {c.steps?.length || 0} step(s) · {c.active === false ? "Paused" : "Active"}</div>
            </li>
          ))}
        </ul>
      </Box>
      <div className="mt-3">
        <Box title="Recent Sends">
          {(!data.recent_sends || data.recent_sends.length === 0) && <Empty>No recent drip activity logged.</Empty>}
          <ul className="divide-y text-sm">
            {(data.recent_sends || []).map((s, i) => (
              <li key={i} className="py-2"><span className="font-semibold">{s.channel || "—"}</span> · {s.customer_id || s.lead_id || ""} <span className="text-xs text-gray-400 ml-2">{s.at ? new Date(s.at).toLocaleString() : ""}</span></li>
            ))}
          </ul>
        </Box>
      </div>
    </Page>
  );
}

// =============================================================================
// PERMISSIONS — granular role × capability matrix (read-only viewer for now)
// =============================================================================
const PERMISSION_GROUPS = [
  { group: "Customers", caps: [
    { k: "view_assigned", label: "View assigned customers" },
    { k: "view_all", label: "View all customers" },
    { k: "edit_all", label: "Edit all customers" },
    { k: "delete", label: "Archive / delete" },
    { k: "export", label: "Export" },
  ]},
  { group: "Deals & Finance", caps: [
    { k: "edit_finance", label: "Edit finance / payoff" },
    { k: "edit_protection", label: "Edit protection products" },
    { k: "mark_sold", label: "Mark sold / lost" },
  ]},
  { group: "Documents", caps: [
    { k: "manage_templates", label: "Manage templates" },
    { k: "deal_jacket", label: "View Deal Jacket" },
    { k: "print_forms", label: "Print forms" },
  ]},
  { group: "Inventory", caps: [
    { k: "edit_inventory", label: "Edit inventory" },
    { k: "run_sync", label: "Run inventory sync" },
  ]},
  { group: "System", caps: [
    { k: "manage_users", label: "Manage users" },
    { k: "manage_settings", label: "Manage settings" },
    { k: "view_audit", label: "View audit logs" },
  ]},
];

const ROLE_DEFAULTS = {
  owner:           "ALL",
  admin:           ["view_all","edit_all","delete","export","edit_finance","edit_protection","mark_sold","manage_templates","deal_jacket","print_forms","edit_inventory","run_sync","manage_users","manage_settings","view_audit"],
  general_manager: ["view_all","edit_all","edit_finance","edit_protection","mark_sold","deal_jacket","print_forms","view_audit"],
  sales_manager:   ["view_all","edit_all","edit_finance","edit_protection","mark_sold","deal_jacket","print_forms","manage_templates"],
  salesperson:     ["view_assigned","deal_jacket","print_forms"],
  bdc:             ["view_assigned"],
  fni:             ["view_all","edit_finance","edit_protection","mark_sold","deal_jacket","print_forms","manage_templates"],
  inventory:       ["edit_inventory","run_sync"],
  marketing:       ["view_all"],
  service:         ["view_assigned"],
  readonly:        ["view_all","deal_jacket"],
  guest:           [],
};
const ROLES = Object.keys(ROLE_DEFAULTS);

export function PermissionsPage() {
  const { user } = useAuth();
  const [matrix, setMatrix] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!["owner", "admin"].includes(user?.role)) return;
    api.get("/security/permissions").then(r => setMatrix(r.data?.matrix || ROLE_DEFAULTS))
       .catch(() => setMatrix(ROLE_DEFAULTS));
  }, [user]);

  if (!["owner", "admin"].includes(user?.role)) {
    return <Page title="Permissions" subtitle="Access denied" testid="page-permissions"><Empty>Owner / Admin only.</Empty></Page>;
  }
  const m = matrix || ROLE_DEFAULTS;
  const has = (role, cap) => m[role] === "ALL" || (m[role] || []).includes(cap);

  const toggle = async (role, cap) => {
    if (!editMode) return;
    if (m[role] === "ALL") return;  // Owner always has all
    const granted = !has(role, cap);
    setSaving(`${role}-${cap}`); setErr("");
    try {
      const r = await api.post("/security/permissions/toggle", { role, capability: cap, granted });
      setMatrix(r.data?.matrix || m);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving("");
    }
  };

  return (
    <Page title="Permissions" subtitle="Role × capability matrix. Toggle a cell to grant/revoke." testid="page-permissions">
      <div className="flex items-center gap-3 mb-3">
        <button
          onClick={() => setEditMode(v => !v)}
          className={`text-sm px-3 py-1.5 rounded font-semibold ${editMode ? "bg-amber-100 text-amber-800 border border-amber-300" : "bg-blue-600 text-white"}`}
          data-testid="perm-edit-toggle"
        >
          {editMode ? "Exit edit mode" : "Edit permissions"}
        </button>
        {editMode && <span className="text-xs text-amber-700">Click any ✓ / — to toggle. Owner always has full access.</span>}
        {err && <span className="text-xs text-red-600">{err}</span>}
      </div>
      <div className="bg-white border rounded-lg overflow-x-auto">
        <table className="w-full text-xs" data-testid="permission-matrix">
          <thead className="bg-gray-50">
            <tr>
              <th className="p-2 text-left sticky left-0 bg-gray-50">Capability</th>
              {ROLES.map(r => <th key={r} className="p-2 text-center min-w-[80px]">{r}</th>)}
            </tr>
          </thead>
          <tbody>
            {PERMISSION_GROUPS.map(g => (
              <>
                <tr key={g.group} className="bg-blue-50"><td colSpan={ROLES.length + 1} className="p-1 font-bold text-blue-700 sticky left-0">{g.group}</td></tr>
                {g.caps.map(c => (
                  <tr key={c.k} className="border-t">
                    <td className="p-2 sticky left-0 bg-white">{c.label}</td>
                    {ROLES.map(r => {
                      const on = has(r, c.k);
                      const isOwner = m[r] === "ALL";
                      const busy = saving === `${r}-${c.k}`;
                      return (
                        <td key={r} className="text-center p-0.5" data-testid={`perm-${r}-${c.k}`}>
                          <button
                            type="button"
                            onClick={() => toggle(r, c.k)}
                            disabled={!editMode || isOwner || busy}
                            className={`w-7 h-6 rounded text-xs font-bold ${editMode && !isOwner ? "hover:bg-gray-100 cursor-pointer" : "cursor-default"} ${on ? "text-emerald-600" : "text-gray-300"}`}
                            data-testid={`perm-cell-${r}-${c.k}`}
                            aria-label={`${r} ${c.k} ${on ? 'granted' : 'denied'}`}
                          >
                            {busy ? "…" : (on ? "✓" : "—")}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-gray-500 mt-3">
        Toggles are persisted to <code>system_settings.permissions</code> and read by all backend role-gate checks. Owner role always retains every capability.
      </div>
    </Page>
  );
}

// =============================================================================
// Layout helpers — Lovable command-center style
// =============================================================================
function Page({ title, subtitle, testid, actions, children }) {
  return (
    <div className="bg-slate-50 min-h-full" data-testid={testid}>
      <div className="bg-gradient-to-b from-[#061426] to-[#0a1f3a] text-white border-b border-white/10">
        <div className="px-5 py-5 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">{title}</h1>
            {subtitle && <p className="text-sm text-blue-200/70 mt-1">{subtitle}</p>}
          </div>
          {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
        </div>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}
function Loading({ children }) { return <div className="p-10 text-center text-gray-400" data-testid="page-loading">{children}</div>; }
function Empty({ children }) { return <div className="p-4 text-center text-sm text-gray-400">{children}</div>; }
function Kpi({ label, value, accent, hint }) {
  const stripe = accent === "red" ? "bg-red-500" :
                 accent === "amber" ? "bg-amber-500" :
                 accent === "emerald" ? "bg-emerald-500" :
                 accent === "blue" ? "bg-blue-500" :
                 "bg-slate-300";
  return (
    <div className="relative rounded-lg border border-gray-200 bg-white p-3 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
      <div className={`absolute top-0 left-0 bottom-0 w-1 ${stripe}`}></div>
      <div className="pl-2">
        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
        <div className="text-2xl font-extrabold text-[#061426] mt-0.5">{value ?? 0}</div>
        {hint && <div className="text-[11px] text-gray-400 mt-0.5">{hint}</div>}
      </div>
    </div>
  );
}
function Box({ title, children, actions }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
      {(title || actions) && (
        <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100">
          {title && <h3 className="text-xs uppercase tracking-wider text-[#061426] font-bold">{title}</h3>}
          {actions && <div className="flex gap-1.5">{actions}</div>}
        </div>
      )}
      {children}
    </div>
  );
}
function FilterChip({ active, onClick, testid, children }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`text-xs px-3 py-1.5 rounded-full border font-semibold transition-colors ${active ? "bg-[#061426] text-white border-[#061426]" : "bg-white text-gray-700 border-gray-300 hover:border-blue-400"}`}>
      {children}
    </button>
  );
}
